import { SerpMatchCell } from "./SerpMatchCell";
import { KeywordTrend } from "@/components/ui/keyword-trend";
import { TableCell } from "@/components/ui/table";
import { RankingCell } from "@/components/keyword-landscape-output/RankingCell";
import { UtilizationCell, SourceCell } from "./TagCells";
import { KpsBreakdownPopover, keywordToKpsBreakdown } from "./KpsBreakdownPopover";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import { formatKeywordPendingNumber, formatKeywordPendingSalesMoney, formatKeywordPendingPercent } from "@/utils/keywordPendingDisplay";
import type { Keyword } from "@/types/keyword";

/**
 * These metric columns are rendered identically in both KeywordBankTable's
 * flat row and KeywordRow's grouped row -- same data, same formatting, no
 * behavioral differences. Shared here so there's exactly one place to change
 * them instead of two. Split into two components (not one) because the Theme
 * cell sits between Trend and Utilization in the column order, and Theme
 * itself genuinely differs between the two views (grouped view supports
 * moving between themes with optimistic updates; flat view only supports
 * adding) so it stays separate in each caller, not part of this shared piece.
 * The other columns that also genuinely differ (checkbox, master-list star,
 * keyword name, actions menu) likewise stay separate in each caller.
 */
export function KeywordMetricCells({
  keyword,
  variant = "bank",
  showSerpMatch = true,
}: {
  keyword: Keyword;
  /** `landscape` drops Theme, Utilization and Source. */
  variant?: "bank" | "landscape";
  /**
   * AUT-496. False only where the values cannot be there: the public share payload leaves the
   * SERP Match fields out, so the column would be dashes for every row forever.
   */
  showSerpMatch?: boolean;
}) {
  return (
    <>
      <TableCell className="w-[90px] py-0 px-0">
        <div className="flex items-center justify-center gap-1.5">
          <RankingCell
            rank={keyword.hero_rank}
            isHeroColumn
            siblingAsin={keyword.hero_rank_from_sibling === true ? keyword.hero_rank_asin : null}
          />
        </div>
      </TableCell>
      <TableCell className="w-[64px] py-2 px-2 text-center">
        <div className="flex items-center justify-center gap-1 text-sm text-foreground font-medium">
          {formatKeywordPendingNumber(keyword.kps)}
          <KpsBreakdownPopover viewModel={keywordToKpsBreakdown(keyword)} />
        </div>
      </TableCell>
      <TableCell className="w-[110px] py-2 px-2 text-center text-sm text-foreground">
        {keyword.keyword_sales != null ? formatKeywordPendingSalesMoney(keyword.keyword_sales) : "—"}
      </TableCell>
      <TableCell className="w-[110px] py-2 px-2 text-center text-sm text-foreground">
        {formatMetricNumber(keyword.search_volume)}
      </TableCell>
      <TableCell className="w-[110px] py-2 px-2 text-center text-sm text-foreground">
        {formatKeywordPendingPercent(keyword.asin_density)}
      </TableCell>
      <TableCell className="w-[120px] py-2 px-2 text-center text-sm text-foreground">
        {keyword.competitors != null ? formatMetricNumber(keyword.competitors) : "—"}
      </TableCell>
      <TableCell className="w-[60px] py-2 px-2 text-center text-sm text-foreground font-medium">
        {formatKeywordPendingNumber(keyword.hero_relevance)}
      </TableCell>
      {/* AUT-496: immediately after Rel., so the two relevance signals sit together.
          On both variants. It was bank-only on the claim that a landscape's keywords are not
          scored — they are: `STAGE_SERP_MATCH_PAGE_ONE` stages every `landscape_keywords` id of the
          product with no `landscape_id` filter, deliberately, because 46% of rows share their
          search with another row of the same product. What was missing was the payload, not the
          data. */}
      {showSerpMatch && (
        <TableCell className="w-[90px] py-2 px-2 text-center text-sm">
          <SerpMatchCell keyword={keyword} />
        </TableCell>
      )}
      <TableCell className="w-[84px] min-w-[84px] py-2 px-1 text-center text-sm overflow-hidden">
        <KeywordTrend value={keyword.search_volume_trend} />
      </TableCell>
    </>
  );
}

export function KeywordTagCells({
  keyword,
  onOpenLandscape,
}: {
  keyword: Keyword;
  onOpenLandscape?: (landscapeId: number) => void;
}) {
  return (
    <>
      <TableCell className="w-[123px] py-1.5 px-2">
        <div className="flex items-center justify-center">
          <UtilizationCell keyword={keyword} />
        </div>
      </TableCell>
      <TableCell className="w-[138px] py-1.5 px-2">
        <div className="flex items-center justify-center">
          <SourceCell keyword={keyword} onOpenLandscape={onOpenLandscape} />
        </div>
      </TableCell>
    </>
  );
}
